import { useMemo } from "react";

import {
  ColumnVisibilityDropdown,
  VisibilityState,
  visibilityStateUrlSerializer,
} from "@/components/ColumnVisibilityToggle";
import { DataTable } from "@/components/DataTable";
import { getSosEntityHashId } from "@/components/SosEntityHashLink";
import { TableWrapper } from "@/components/TableWrapper";
import { PersistedStateActionButtons, usePersistedState } from "@/hooks/usePersistedState";
import { BomComponent } from "../../queries/tracking-data/bom-by-component";
import { BomComponentTableRow, getBomByComponentTableColumns } from "./bom-by-component-table-columns";
import { usePurchasingNotesByItemId } from "../purchasing-progress/usePurchasingNotesByItemId";

export interface BomByComponentProps {
  bomComponents: BomComponent[];

  /**
   * Used to determine whether job-specific columns should be shown.
   * Pass null when no job context exists (i.e. estimate tracking).
   */
  jobId: number | null;
}

export const BomByComponent: React.FC<BomByComponentProps> = ({ bomComponents, jobId }) => {
  const [columnVisibility, setColumnVisibility, persistedStateActions] = usePersistedState({
    componentId: "bom-by-component-hidden-cols",
    urlKey: "hidden-cols",
    stateSchema: VisibilityState,
    version: 1,
    defaultState: {},
    urlSerializer: visibilityStateUrlSerializer,
  });

  // Fetch all purchasing notes for the current job, if a jobId is provided
  const notesByItemIdQuery = usePurchasingNotesByItemId(jobId);

  // Generate the table row data
  const data = useMemo(
    () =>
      bomComponents.map<BomComponentTableRow>((component) => {
        const baseRow: BomComponentTableRow = {
          rowId: getSosEntityHashId("item", component.item.id),
          numToPurchase: component.isAssembly ? undefined : component.nonInventoryQuantity,
          numToBuild: component.isAssembly ? component.nonInventoryQuantity : undefined,
          ...component,
        };

        if (jobId === null) return baseRow;

        return {
          ...baseRow,
          jobId,
          initialNote:
            notesByItemIdQuery.isLoading ? undefined
            : notesByItemIdQuery.error ? notesByItemIdQuery.error
            : (notesByItemIdQuery.data?.get(component.item.id) ?? null),
        };
      }),
    [bomComponents, jobId, notesByItemIdQuery],
  );

  const bomByComponentTableColumns = getBomByComponentTableColumns(jobId);

  return (
    <>
      <div className="col-auto mb-3">
        <div className="row row-cols-auto gx-3 align-items-center">
          <ColumnVisibilityDropdown
            columns={bomByComponentTableColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
          <PersistedStateActionButtons {...persistedStateActions} />
        </div>
      </div>
      <TableWrapper className="col">
        <DataTable
          bordered
          hover
          data={data}
          columns={bomByComponentTableColumns}
          getRowId={({ rowId }) => rowId}
          initialState={{
            sorting: [
              { id: "onSalesOrder", desc: true },
              { id: "item_type", desc: true },
              { id: "item_name", desc: false },
            ],
          }}
          state={{ columnVisibility }}
        />
      </TableWrapper>
    </>
  );
};
